/**
 * AgentParameterEditor - Editor for agent parameter schema
 *
 * Allows users to define the JSON schema for parameters that the AI agent
 * will extract from the conversation to pass to this tool.
 *
 * Uses a card-based expandable UI where each parameter is displayed as a
 * collapsible card with type badge, name, and description preview.
 */

import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Text,
  Input,
  Dropdown,
  Option,
  Field,
  MessageBar,
  MessageBarBody,
  Tab,
  TabList,
  Tooltip,
  Textarea,
  mergeClasses,
} from '@fluentui/react-components';
import {
  AddRegular,
  DeleteRegular,
  CodeRegular,
  FormRegular,
  InfoRegular,
  ChevronDownRegular,
  ChevronUpRegular,
  DocumentAddRegular,
} from '@fluentui/react-icons';
import { EditorState, Compartment, type Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { json } from '@codemirror/lang-json';
import { bracketMatching, foldGutter, foldKeymap } from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { search, searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { useAppStore } from '@/stores/appStore';
import { createFluentTheme, formatJsonKeymap, wrapSelectionInputHandler } from '@/components/WorkflowDesigner/CodeEditor/extensions';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    minWidth: 0, // Prevent flex overflow issues
    width: '100%', // Fill available width
    maxWidth: '100%', // Prevent overflow
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    width: '100%',
    boxSizing: 'border-box',
    flexWrap: 'wrap', // Allow wrapping on narrow containers
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  infoIcon: {
    color: tokens.colorNeutralForeground3,
    cursor: 'help',
    fontSize: tokens.fontSizeBase300,
  },
  tabList: {
    minWidth: 0,
  },
  hint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase200,
    marginBottom: tokens.spacingVerticalXS,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  // Card styles
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0, // Prevent flex overflow
    width: '100%',
    maxWidth: '100%',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    borderRadius: tokens.borderRadiusMedium,
    transitionProperty: 'border-color, box-shadow',
    transitionDuration: '0.15s',
    minWidth: 0, // Prevent flex overflow
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  cardInteractive: {
    ':hover': {
      ...shorthands.borderColor(tokens.colorNeutralStroke1Hover),
      boxShadow: tokens.shadow2,
    },
  },
  cardExpanded: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    boxShadow: tokens.shadow4,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  cardHeaderReadOnly: {
    cursor: 'default',
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  // Type badge - subtle, muted, compact
  typeBadge: {
    fontSize: '9px',
    fontWeight: tokens.fontWeightMedium,
    paddingTop: '1px',
    paddingBottom: '1px',
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    borderRadius: tokens.borderRadiusSmall,
    textTransform: 'uppercase',
    letterSpacing: '0.2px',
    flexShrink: 0,
  },
  typeBadgeString: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    color: tokens.colorPaletteBlueForeground2,
  },
  typeBadgeNumber: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  typeBadgeBoolean: {
    backgroundColor: tokens.colorPalettePurpleBackground2,
    color: tokens.colorPalettePurpleForeground2,
  },
  typeBadgeObject: {
    backgroundColor: tokens.colorPaletteMarigoldBackground2,
    color: tokens.colorPaletteMarigoldForeground2,
  },
  typeBadgeArray: {
    backgroundColor: tokens.colorPalettePinkBackground2,
    color: tokens.colorPalettePinkForeground2,
  },
  typeBadgeDefault: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  cardInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  cardNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  cardName: {
    fontWeight: tokens.fontWeightMedium,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyMonospace,
  },
  cardMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXXS,
  },
  metaSeparator: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase200,
  },
  cardDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardDescriptionEmpty: {
    fontStyle: 'italic',
    color: tokens.colorNeutralForeground4,
  },
  chevronIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    fontSize: tokens.fontSizeBase400,
  },
  // Expanded card body
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    width: '100%',
    boxSizing: 'border-box',
  },
  cardFieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 100px',
    gap: tokens.spacingHorizontalS,
    alignItems: 'start',
    width: '100%',
    boxSizing: 'border-box',
  },
  cardFieldName: {
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
  },
  cardFieldType: {
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
    // Force dropdown to fit container width
    '& .fui-Dropdown': {
      minWidth: 0,
      width: '100%',
    },
    '& button': {
      minWidth: 0,
      width: '100%',
    },
  },
  cardFieldFull: {
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Delete confirmation
  deleteConfirmation: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  deleteConfirmText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  // Validation error
  validationError: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalXXS,
  },
  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'dashed', tokens.colorNeutralStroke2),
  },
  emptyStateIcon: {
    fontSize: tokens.fontSizeHero700,
    color: tokens.colorNeutralForeground4,
  },
  emptyStateTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  emptyStateHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    maxWidth: '260px',
  },
  deleteButton: {
    backgroundColor: tokens.colorPaletteRedBackground3,
  },
  // JSON editor styles
  jsonEditorContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  jsonEditorWrapper: {
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    overflow: 'hidden',
    minHeight: '200px',
    maxHeight: '400px',
    '& .cm-editor': {
      height: '100%',
      minHeight: '200px',
      maxHeight: '400px',
    },
    '& .cm-scroller': {
      minHeight: '200px',
      maxHeight: '400px',
    },
  },
  jsonEditorError: {
    ...shorthands.borderColor(tokens.colorPaletteRedBorder1),
  },
  // Add button at bottom
  addButton: {
    alignSelf: 'flex-start',
  },
});

interface SchemaProperty {
  type: string;
  description?: string;
}

interface AgentParameterSchema {
  type: 'object';
  properties: Record<string, SchemaProperty>;
  required?: string[];
}

interface AgentParameterEditorProps {
  /** Current schema value */
  schema?: Record<string, unknown> | undefined;
  /** Callback when schema changes */
  onChange?: (schema: Record<string, unknown>) => void;
  /** Whether the editor is read-only */
  readOnly?: boolean;
}

const PARAMETER_TYPES = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'object', label: 'Object' },
  { value: 'array', label: 'Array' },
] as const;

type EditorMode = 'form' | 'json';

/**
 * Validate JSON schema structure
 */
function validateSchema(schemaText: string): { valid: boolean; error?: string; schema?: AgentParameterSchema } {
  if (!schemaText.trim()) {
    return { valid: true, schema: { type: 'object', properties: {} } };
  }

  try {
    const parsed = JSON.parse(schemaText);

    if (typeof parsed !== 'object' || parsed === null) {
      return { valid: false, error: 'Schema must be a JSON object' };
    }

    if (parsed.type && parsed.type !== 'object') {
      return { valid: false, error: 'Schema type must be "object"' };
    }

    if (parsed.properties && typeof parsed.properties !== 'object') {
      return { valid: false, error: 'Properties must be an object' };
    }

    if (parsed.required && !Array.isArray(parsed.required)) {
      return { valid: false, error: 'Required must be an array' };
    }

    return { valid: true, schema: parsed as AgentParameterSchema };
  } catch (e) {
    const err = e as Error;
    return { valid: false, error: `Invalid JSON: ${err.message}` };
  }
}

interface ParsedProperty {
  name: string;
  type: string;
  description: string | undefined;
  required: boolean;
}

/**
 * Parse schema into property list for display
 */
function parseSchemaProperties(schema: Record<string, unknown> | undefined): ParsedProperty[] {
  if (!schema) return [];

  const properties = schema.properties as Record<string, SchemaProperty> | undefined;
  const required = schema.required as string[] | undefined;

  if (!properties) return [];

  return Object.entries(properties).map(([name, prop]): ParsedProperty => ({
    name,
    type: prop.type || 'string',
    description: prop.description,
    required: required?.includes(name) ?? false,
  }));
}

/**
 * Get the type badge style class
 */
function getTypeBadgeClass(type: string, styles: ReturnType<typeof useStyles>): string {
  switch (type) {
    case 'string': return styles.typeBadgeString;
    case 'number':
    case 'integer': return styles.typeBadgeNumber;
    case 'boolean': return styles.typeBadgeBoolean;
    case 'object': return styles.typeBadgeObject;
    case 'array': return styles.typeBadgeArray;
    default: return styles.typeBadgeDefault;
  }
}

/**
 * Validate parameter name
 */
function validateParameterName(
  name: string,
  existingNames: string[],
  currentName?: string
): string | null {
  if (!name.trim()) {
    return 'Name is required';
  }

  // Check for valid identifier pattern (start with letter or underscore, then letters/numbers/underscores)
  const validPattern = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
  if (!validPattern.test(name)) {
    return 'Name must start with a letter or underscore and contain only letters, numbers, and underscores';
  }

  // Check for duplicates (excluding current name if editing)
  const otherNames = currentName
    ? existingNames.filter(n => n !== currentName)
    : existingNames;
  if (otherNames.includes(name)) {
    return 'A parameter with this name already exists';
  }

  return null;
}

/**
 * Generate a unique parameter name
 */
function generateUniqueName(existingNames: string[]): string {
  if (!existingNames.includes('param')) {
    return 'param';
  }

  let counter = 1;
  while (existingNames.includes(`param${counter}`)) {
    counter++;
  }
  return `param${counter}`;
}

/**
 * Hook to manage CodeMirror editor for JSON
 */
function useJsonEditor(
  containerRef: React.RefObject<HTMLDivElement | null>,
  initialValue: string,
  onChange: (value: string) => void,
  theme: 'light' | 'dark',
  isActive: boolean
) {
  const viewRef = useRef<EditorView | null>(null);
  const themeCompartment = useRef(new Compartment());
  const isUpdatingRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const initialValueRef = useRef(initialValue);
  onChangeRef.current = onChange;

  // Only update initialValueRef when not active (to avoid re-creating editor)
  if (!isActive) {
    initialValueRef.current = initialValue;
  }

  useEffect(() => {
    // Only create editor when active and container exists
    if (!isActive || !containerRef.current) {
      // Cleanup when becoming inactive
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
      return;
    }

    // Already have an editor
    if (viewRef.current) {
      return;
    }

    const extensions: Extension[] = [
      lineNumbers(),
      history(),
      foldGutter({
        openText: '▼',
        closedText: '▶',
      }),
      bracketMatching(),
      wrapSelectionInputHandler(),
      closeBrackets(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      search({ top: true }),
      json(),
      EditorState.tabSize.of(2),
      keymap.of([
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...searchKeymap,
        ...formatJsonKeymap,
        indentWithTab,
      ]),
      themeCompartment.current.of(createFluentTheme(theme)),
      EditorView.updateListener.of((update) => {
        if (update.docChanged && !isUpdatingRef.current) {
          onChangeRef.current(update.state.doc.toString());
        }
      }),
    ];

    const state = EditorState.create({
      doc: initialValueRef.current,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [isActive, containerRef, theme]);

  useEffect(() => {
    if (!viewRef.current) return;
    viewRef.current.dispatch({
      effects: themeCompartment.current.reconfigure(createFluentTheme(theme)),
    });
  }, [theme]);

  const setValue = useCallback((value: string) => {
    if (!viewRef.current) return;
    const currentValue = viewRef.current.state.doc.toString();
    if (currentValue === value) return;

    isUpdatingRef.current = true;
    viewRef.current.dispatch({
      changes: {
        from: 0,
        to: viewRef.current.state.doc.length,
        insert: value,
      },
    });
    isUpdatingRef.current = false;
  }, []);

  return { setValue };
}

// ============================================================================
// ParameterCard Component
// ============================================================================

interface ParameterCardProps {
  property: ParsedProperty;
  existingNames: string[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (updates: { name?: string; type?: string; description?: string; required?: boolean }) => void;
  onDelete: () => void;
  readOnly: boolean;
}

const ParameterCard = ({
  property,
  existingNames,
  isExpanded,
  onToggleExpand,
  onUpdate,
  onDelete,
  readOnly,
}: ParameterCardProps) => {
  const styles = useStyles();
  const cardRef = useRef<HTMLDivElement>(null);

  // Local state for editing (prevents cursor jump during typing)
  const [localName, setLocalName] = useState(property.name);
  const [localDescription, setLocalDescription] = useState(property.description || '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Sync local state when property changes externally
  useEffect(() => {
    setLocalName(property.name);
    setLocalDescription(property.description || '');
  }, [property.name, property.description]);

  // Scroll into view when card expands
  useEffect(() => {
    if (isExpanded && cardRef.current) {
      // Small delay to let the DOM render the expanded content
      requestAnimationFrame(() => {
        // scrollIntoView may not exist in test environments (jsdom)
        cardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }, [isExpanded]);

  // Validate and commit name on blur
  const handleNameBlur = useCallback(() => {
    const error = validateParameterName(localName, existingNames, property.name);
    setNameError(error);

    if (!error && localName !== property.name) {
      onUpdate({ name: localName });
    } else if (error) {
      // Reset to original if invalid
      setLocalName(property.name);
      setNameError(null);
    }
  }, [localName, existingNames, property.name, onUpdate]);

  // Validate name as user types
  const handleNameChange = useCallback((value: string) => {
    setLocalName(value);
    const error = validateParameterName(value, existingNames, property.name);
    setNameError(error);
  }, [existingNames, property.name]);

  // Commit description on blur
  const handleDescriptionBlur = useCallback(() => {
    if (localDescription !== (property.description || '')) {
      onUpdate({ description: localDescription });
    }
  }, [localDescription, property.description, onUpdate]);

  // Handle type change immediately
  const handleTypeChange = useCallback((type: string) => {
    onUpdate({ type });
  }, [onUpdate]);

  // Handle delete confirmation
  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    onDelete();
  }, [onDelete]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const ariaLabel = `${property.name} parameter, ${property.type} type. ${readOnly ? '' : 'Click to expand.'}`;

  return (
    <div
      ref={cardRef}
      className={mergeClasses(
        styles.card,
        !readOnly && styles.cardInteractive,
        isExpanded && styles.cardExpanded
      )}
    >
      {/* Card Header - Collapsed View */}
      <div
        className={mergeClasses(
          styles.cardHeader,
          readOnly && styles.cardHeaderReadOnly
        )}
        onClick={readOnly ? undefined : onToggleExpand}
        onKeyDown={readOnly ? undefined : (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpand();
          }
        }}
        role={readOnly ? undefined : 'button'}
        tabIndex={readOnly ? undefined : 0}
        aria-expanded={readOnly ? undefined : isExpanded}
        aria-label={ariaLabel}
        data-testid={`parameter-card-${property.name}`}
      >
        {/* Name, Type, and Description */}
        <div className={styles.cardInfo}>
          <div className={styles.cardNameRow}>
            <Text className={styles.cardName}>{property.name}</Text>
          </div>
          <div className={styles.cardMeta}>
            <span className={mergeClasses(styles.typeBadge, getTypeBadgeClass(property.type, styles))}>
              {property.type}
            </span>
            {property.description && (
              <>
                <span className={styles.metaSeparator}>·</span>
                <Text
                  className={mergeClasses(
                    styles.cardDescription,
                    !property.description && styles.cardDescriptionEmpty
                  )}
                >
                  {property.description}
                </Text>
              </>
            )}
          </div>
        </div>

        {/* Chevron Icon */}
        {!readOnly && (
          isExpanded ? (
            <ChevronUpRegular className={styles.chevronIcon} />
          ) : (
            <ChevronDownRegular className={styles.chevronIcon} />
          )
        )}
      </div>

      {/* Card Body - Expanded View */}
      {isExpanded && !readOnly && (
        <div className={styles.cardBody}>
          {/* Name and Type Row */}
          <div className={styles.cardFieldRow}>
            <div className={styles.cardFieldName}>
              <Field
                label="Name"
                required
                validationState={nameError ? 'error' : 'none'}
                validationMessage={nameError || null}
              >
                <Input
                  value={localName}
                  onChange={(_, data) => handleNameChange(data.value)}
                  onBlur={handleNameBlur}
                  placeholder="parameterName"
                />
              </Field>
            </div>
            <div className={styles.cardFieldType}>
              <Field label="Type">
                <Dropdown
                  value={PARAMETER_TYPES.find(t => t.value === property.type)?.label || 'String'}
                  selectedOptions={[property.type]}
                  onOptionSelect={(_, data) => handleTypeChange(data.optionValue || 'string')}
                >
                  {PARAMETER_TYPES.map(t => (
                    <Option key={t.value} value={t.value}>{t.label}</Option>
                  ))}
                </Dropdown>
              </Field>
            </div>
          </div>

          {/* Description Row */}
          <div className={styles.cardFieldFull}>
            <Field label="Description">
              <Textarea
                value={localDescription}
                onChange={(_, data) => setLocalDescription(data.value)}
                onBlur={handleDescriptionBlur}
                placeholder="Brief description of what this parameter is for"
                resize="vertical"
                rows={2}
              />
            </Field>
          </div>

          {/* Footer with Delete */}
          <div className={styles.cardFooter}>
            {showDeleteConfirm ? (
              <div className={styles.deleteConfirmation}>
                <Text className={styles.deleteConfirmText}>Delete this parameter?</Text>
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={handleCancelDelete}
                >
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  size="small"
                  onClick={handleConfirmDelete}
                  className={styles.deleteButton}
                >
                  Delete
                </Button>
              </div>
            ) : (
              <Button
                appearance="subtle"
                size="small"
                icon={<DeleteRegular />}
                onClick={handleDeleteClick}
                aria-label={`Delete ${property.name} parameter`}
              >
                Delete
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const AgentParameterEditor = ({
  schema,
  onChange,
  readOnly = false,
}: AgentParameterEditorProps) => {
  const styles = useStyles();
  const theme = useAppStore((s) => s.theme);
  const [mode, setMode] = useState<EditorMode>('form');
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // JSON editor ref
  const jsonEditorRef = useRef<HTMLDivElement>(null);

  // Parse properties from schema for visual display
  const properties = useMemo(() => parseSchemaProperties(schema), [schema]);
  const propertyNames = useMemo(() => properties.map(p => p.name), [properties]);

  // Handle JSON editor changes
  const handleJsonChange = useCallback((value: string) => {
    const result = validateSchema(value);
    if (result.valid) {
      setValidationError(null);
      onChange?.(result.schema as unknown as Record<string, unknown>);
    } else {
      setValidationError(result.error ?? 'Invalid JSON');
    }
  }, [onChange]);

  // Initialize JSON editor (only active when in JSON mode)
  const { setValue: setJsonEditorValue } = useJsonEditor(
    jsonEditorRef,
    schema ? JSON.stringify(schema, null, 2) : '{\n  "type": "object",\n  "properties": {},\n  "required": []\n}',
    handleJsonChange,
    theme,
    mode === 'json'
  );

  // Sync JSON value when switching to JSON mode or schema changes
  useEffect(() => {
    if (mode === 'json') {
      const newValue = schema
        ? JSON.stringify(schema, null, 2)
        : '{\n  "type": "object",\n  "properties": {},\n  "required": []\n}';
      setJsonEditorValue(newValue);
    }
  }, [mode, schema, setJsonEditorValue]);

  // Handle mode change
  const handleModeChange = useCallback((_: unknown, data: { value: unknown }) => {
    const newMode = data.value as EditorMode;
    setMode(newMode);
    setValidationError(null);
    setExpandedCard(null);
  }, []);

  // Toggle card expansion
  const handleToggleExpand = useCallback((name: string) => {
    setExpandedCard(prev => prev === name ? null : name);
  }, []);

  // Add new parameter
  const handleAddParameter = useCallback(() => {
    const newName = generateUniqueName(propertyNames);

    const currentSchema = schema || { type: 'object', properties: {}, required: [] };
    const newSchema = {
      ...currentSchema,
      type: 'object' as const,
      properties: {
        ...(currentSchema.properties as Record<string, unknown>),
        [newName]: {
          type: 'string',
        },
      },
      required: (currentSchema.required as string[]) || [],
    };

    onChange?.(newSchema as Record<string, unknown>);
    // Auto-expand the new parameter for immediate editing
    setExpandedCard(newName);
  }, [propertyNames, schema, onChange]);

  // Update parameter
  const handleUpdateParameter = useCallback((
    oldName: string,
    updates: { name?: string; type?: string; description?: string; required?: boolean }
  ) => {
    if (!schema) return;

    const newSchema = { ...schema };
    const properties = { ...(newSchema.properties as Record<string, SchemaProperty>) };
    const required = [...((newSchema.required as string[]) ?? [])];

    // Handle name change (need to recreate the property with new key)
    if (updates.name && updates.name !== oldName) {
      const oldProp = properties[oldName];
      if (oldProp) {
        delete properties[oldName];
        const newProp: SchemaProperty = { type: oldProp.type };
        if (oldProp.description) {
          newProp.description = oldProp.description;
        }
        properties[updates.name] = newProp;

        // Update required array
        const requiredIndex = required.indexOf(oldName);
        if (requiredIndex !== -1) {
          required[requiredIndex] = updates.name;
        }

        // Update expanded card to track new name
        setExpandedCard(updates.name);
      }
    }

    const propName = updates.name || oldName;
    const currentProp = properties[propName];
    if (!currentProp) return;

    // Update type
    if (updates.type !== undefined) {
      const updatedProp: SchemaProperty = { type: updates.type };
      if (currentProp.description) {
        updatedProp.description = currentProp.description;
      }
      properties[propName] = updatedProp;
    }

    // Update description
    if (updates.description !== undefined) {
      if (updates.description) {
        properties[propName] = { type: currentProp.type, description: updates.description };
      } else {
        // Remove description if empty
        properties[propName] = { type: currentProp.type };
      }
    }

    // Update required
    if (updates.required !== undefined) {
      const idx = required.indexOf(propName);
      if (updates.required && idx === -1) {
        required.push(propName);
      } else if (!updates.required && idx !== -1) {
        required.splice(idx, 1);
      }
    }

    newSchema.properties = properties;
    newSchema.required = required;
    onChange?.(newSchema);
  }, [schema, onChange]);

  // Remove parameter
  const handleRemoveParameter = useCallback((paramName: string) => {
    if (!schema) return;

    const newSchema = { ...schema };
    const properties = { ...(newSchema.properties as Record<string, unknown>) };
    delete properties[paramName];
    newSchema.properties = properties;

    const required = (newSchema.required as string[]) ?? [];
    newSchema.required = required.filter(r => r !== paramName);

    onChange?.(newSchema);
    setExpandedCard(null);
  }, [schema, onChange]);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Text className={styles.title}>Agent Parameters</Text>
          <Tooltip
            content="Define parameters the AI agent will extract from the conversation context to pass to this tool"
            relationship="description"
          >
            <InfoRegular className={styles.infoIcon} />
          </Tooltip>
        </div>
        {!readOnly && (
          <TabList
            className={styles.tabList}
            selectedValue={mode}
            onTabSelect={handleModeChange}
            size="small"
          >
            <Tab value="form" icon={<FormRegular />}>Form</Tab>
            <Tab value="json" icon={<CodeRegular />}>JSON</Tab>
          </TabList>
        )}
      </div>

      <Text className={styles.hint}>
        {mode === 'form'
          ? 'Add parameters the agent should extract from the conversation.'
          : 'Edit the JSON schema directly for advanced configuration.'}
      </Text>

      <div className={styles.content}>
        {mode === 'form' ? (
          <>
            {/* Parameter Cards */}
            {properties.length > 0 && (
              <div className={styles.cardList}>
                {properties.map((prop) => (
                  <ParameterCard
                    key={prop.name}
                    property={prop}
                    existingNames={propertyNames}
                    isExpanded={expandedCard === prop.name}
                    onToggleExpand={() => handleToggleExpand(prop.name)}
                    onUpdate={(updates) => handleUpdateParameter(prop.name, updates)}
                    onDelete={() => handleRemoveParameter(prop.name)}
                    readOnly={readOnly}
                  />
                ))}
              </div>
            )}

            {/* Empty state or Add button */}
            {!readOnly && (
              properties.length === 0 ? (
                <div className={styles.emptyState}>
                  <DocumentAddRegular className={styles.emptyStateIcon} />
                  <Text className={styles.emptyStateTitle}>No parameters defined</Text>
                  <Text className={styles.emptyStateHint}>
                    Add parameters that the agent should extract from the conversation to pass to this tool.
                  </Text>
                  <Button
                    appearance="primary"
                    icon={<AddRegular />}
                    onClick={handleAddParameter}
                  >
                    Add Parameter
                  </Button>
                </div>
              ) : (
                <Button
                  className={styles.addButton}
                  appearance="subtle"
                  icon={<AddRegular />}
                  onClick={handleAddParameter}
                >
                  Add Parameter
                </Button>
              )
            )}
          </>
        ) : (
          /* JSON editor mode */
          <div className={styles.jsonEditorContainer}>
            <div
              ref={jsonEditorRef}
              className={mergeClasses(
                styles.jsonEditorWrapper,
                !!validationError && styles.jsonEditorError
              )}
            />
            {validationError && (
              <MessageBar intent="error">
                <MessageBarBody>{validationError}</MessageBarBody>
              </MessageBar>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

AgentParameterEditor.displayName = 'AgentParameterEditor';
